import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import DashboardLayout from "../../../Layout/DashboardLayout";
import {
  Dropdown,
  FileUpload,
  employeeID,
  companyID,
} from "../../../Common/UIComponents";
import {
  saveLocation,
  fetchLocations,
  fetchCountries,
  fetchStates,
  fetchCities,
} from "../../../API_Service/Settings_Slice";
import { URL } from "../../../Common/APIURL";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const toArray = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  return Array.isArray(d) ? d : [];
};

const toOptions = (items = [], labelKey, valueKey) =>
  items.map((item) => ({
    label: item[labelKey] ?? item.Label ?? item.Name ?? item.name ?? String(item),
    value: item[valueKey] ?? item.Value ?? item.ID   ?? item.id   ?? String(item),
  }));

const EMPTY_FORM = {
  country:  "",
  state:    "",
  city:     "",
  cityName: "",
  icon:     null,
};

// ─────────────────────────────────────────────
// FIELD LABEL
// ─────────────────────────────────────────────

const FieldLabel = ({ children, required }) => (
  <label className="block text-xs font-bold text-[#3e494a] uppercase tracking-widest ml-1 mb-2">
    {children}
    {required && <span className="text-[#ba1a1a] ml-1">*</span>}
  </label>
);

// ─────────────────────────────────────────────
// CITY AUTOCOMPLETE
// ─────────────────────────────────────────────

const CITY_NAME_MAX_LENGTH = 20;

const CharCounter = ({ value, max }) => {
  const len = (value ?? "").length;
  if (len === 0) return null;
  const atLimit = len === max;
  return (
    <div className={`flex items-center gap-1 mt-1 text-xs ${atLimit ? "text-red-500 font-semibold" : "text-[#3e494a]/60"}`}>
      <span>{len}/{max}</span>
      {atLimit && <span className="text-red-500">— limit reached</span>}
    </div>
  );
};

const CityAutocomplete = ({ options = [], value, onChange, onNewCity, disabled, error, cityName = "" }) => {
  const [inputVal, setInputVal] = useState("");
  const [showList, setShowList] = useState(false);
  const [isNew,    setIsNew]    = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const previousCityNameRef = useRef(null);
  const wrapRef = useRef(null);

  // When cityName arrives from API, set it immediately and LOCK it
  useEffect(() => {
    if (cityName && cityName.trim() !== "" && cityName !== previousCityNameRef.current) {
      console.log("✅ City name from API - setting:", cityName);
      previousCityNameRef.current = cityName;
      setInputVal(cityName);
      setIsNew(false);
    } else if (!cityName || cityName.trim() === "") {
      // Clear when cityName is empty
      console.log("🔄 Clearing city name");
      previousCityNameRef.current = null;
      setInputVal("");
      setIsNew(false);
    }
  }, [cityName]);

  // Only handle option matching when no cityName from API
  useEffect(() => {
    // If cityName is set from API, NEVER change it
    if (previousCityNameRef.current && previousCityNameRef.current.trim() !== "") {
      return;
    }

    // Try to match value with options
    if (value && value !== "0" && options.length > 0) {
      const matched = options.find((o) => String(o.value) === String(value));
      if (matched) {
        setInputVal(matched.label);
        setIsNew(false);
        return;
      }
    }

    // Clear if no value
    if (!value || value === "0") {
      setInputVal("");
      setIsNew(false);
    }
  }, [value, options]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowList(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    // If not actively typing or list is empty, show all options
    if (!isUserTyping || !inputVal.trim()) {
      console.log("📋 Showing all cities - count:", options.length);
      return options.slice(0, 100);
    }

    // User is actively typing/filtering - show matches
    const matches = options.filter((o) =>
      o.label.toLowerCase().includes(inputVal.trim().toLowerCase())
    ).slice(0, 100);
    console.log("🔍 Filtered cities:", matches.length, "for input:", inputVal);
    return matches;
  }, [inputVal, options, isUserTyping]);

  const handleInput = (e) => {
    const val = e.target.value;
    setInputVal(val);
    setShowList(true);
    setIsUserTyping(true);

    // If user clears the input, show all cities
    if (!val.trim()) {
      onChange("0");
      onNewCity(null);
      setIsNew(false);
      setIsUserTyping(false);
      return;
    }

    // Check if it matches an existing city
    const matched = options.find((o) => o.label.toLowerCase() === val.trim().toLowerCase());
    if (matched) {
      onChange(matched.value);
      onNewCity(null);
      setIsNew(false);
    } else {
      onChange("0");
      onNewCity(val.trim());
      setIsNew(val.trim().length > 0);
    }
  };

  const handleSelect = (opt) => {
    setInputVal(opt.label);
    setShowList(false);
    setIsNew(false);
    setIsUserTyping(false);
    onChange(opt.value);
    onNewCity(null);
  };

  const showNewHint = isNew && inputVal.trim().length > 0 &&
    !options.some((o) => o.label.toLowerCase() === inputVal.trim().toLowerCase());

  return (
    <div ref={wrapRef} className="relative">
      <div className={`flex items-center bg-[#e5e9e9] rounded-xl px-4 py-3 gap-2 overflow-hidden
        focus-within:ring-2 focus-within:ring-[#08757c] focus-within:bg-white transition-all
        ${error ? "ring-2 ring-[#ba1a1a]" : ""}
        ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
        <input
          type="text" value={inputVal} onChange={handleInput}
          onFocus={() => setShowList(true)}
          placeholder="Select or type city..."
          maxLength={CITY_NAME_MAX_LENGTH}
          className="flex-1 min-w-0 bg-transparent outline-none text-sm text-[#181c1d] placeholder:text-[#3e494a]/40 truncate"
          disabled={disabled}
        />
        <span className="material-symbols-outlined text-[#3e494a]/60 text-[18px]">
          {showList ? "keyboard_arrow_up" : "keyboard_arrow_down"}
        </span>
      </div>
      <CharCounter value={inputVal} max={CITY_NAME_MAX_LENGTH} />
      {error && <p className="text-xs text-[#ba1a1a] mt-1 ml-1">{error}</p>}
      {showNewHint && (
        <p className="text-[10px] text-[#436900] mt-1 ml-1 font-medium truncate">
          <span className="material-symbols-outlined text-[12px] align-middle">add_circle</span>
          {" "}"{inputVal.trim()}" will be added as a new city.
        </p>
      )}
      {showList && !disabled && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-xl border border-[#ebeeee] shadow-lg max-h-52 overflow-y-auto">
          {filtered.length === 0 && inputVal.trim() ? (
            <div className="px-4 py-3 text-xs text-[#3e494a]/60 italic">
              No match — "{inputVal.trim()}" will be saved as a new city.
            </div>
          ) : (
            filtered.map((opt) => (
              <button key={opt.value} type="button" onMouseDown={() => handleSelect(opt)}
                className="w-full text-left px-4 py-2.5 text-sm text-[#181c1d] hover:bg-[#f1f4f4] transition-colors">
                {opt.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// LEAFLET MAP PANEL
// ─────────────────────────────────────────────

const MapPanel = ({ cityName, locationQuery, onPinMove }) => {
  const mapRef    = useRef(null);
  const mapObjRef = useRef(null);
  const markerRef = useRef(null);
  const [mapMode,  setMapMode]  = useState("map");
  const [coords,   setCoords]   = useState({ lat: 20.5937, lng: 78.9629 });
  const [accuracy, setAccuracy] = useState("±2.4m");
  const [zone,     setZone]     = useState(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css"; link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
    const loadLeaflet = () =>
      new Promise((resolve) => {
        if (window.L) { resolve(window.L); return; }
        const script = document.createElement("script");
        script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
        script.onload = () => resolve(window.L);
        document.body.appendChild(script);
      });

    loadLeaflet().then((L) => {
      if (!mapRef.current || mapObjRef.current) return;
      const tiles = {
        map: L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OpenStreetMap contributors", maxZoom: 19,
        }),
        satellite: L.tileLayer(
          "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          { attribution: "© Esri", maxZoom: 19 }
        ),
        terrain: L.tileLayer(
          "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
          { attribution: "© OpenTopoMap", maxZoom: 17 }
        ),
      };
      const map = L.map(mapRef.current, {
        center: [20.5937, 78.9629], zoom: 5,
        zoomControl: false, layers: [tiles.map],
      });
      mapObjRef.current = map;
      mapObjRef.current._tiles = tiles;
      const pinIcon = L.divIcon({
        className: "", iconSize: [48, 56], iconAnchor: [24, 56],
        html: `<div style="position:relative;display:flex;flex-direction:column;align-items:center">
          <div style="width:48px;height:48px;border-radius:50%;background:linear-gradient(135deg,#08757c,#005b60);border:4px solid white;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 20px rgba(8,117,124,0.5);color:white;">
            <span class="material-symbols-outlined" style="font-size:22px;line-height:1">location_pin</span>
          </div>
          <div style="width:2px;height:8px;background:linear-gradient(to bottom,white,transparent)"></div>
        </div>`,
      });
      const marker = L.marker([20.5937, 78.9629], { icon: pinIcon, draggable: true }).addTo(map);
      markerRef.current = marker;
      marker.on("dragend", (e) => {
        const { lat, lng } = e.target.getLatLng();
        const c = { lat: Math.round(lat * 10000) / 10000, lng: Math.round(lng * 10000) / 10000 };
        setCoords(c); setAccuracy(`±${(Math.random() * 3 + 1).toFixed(1)}m`);
        setZone(`Coordinates updated: ${c.lat}°, ${c.lng}°`); onPinMove?.(c);
      });
      map.on("click", (e) => {
        const { lat, lng } = e.latlng;
        marker.setLatLng([lat, lng]);
        const c = { lat: Math.round(lat * 10000) / 10000, lng: Math.round(lng * 10000) / 10000 };
        setCoords(c); setZone(`Pin placed at ${c.lat}°, ${c.lng}°`); onPinMove?.(c);
      });
      // Signal that the map is fully ready — triggers any pending location search
      setMapReady(true);
    });
    return () => {
      if (mapObjRef.current) { mapObjRef.current.remove(); mapObjRef.current = null; }
      setMapReady(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapObjRef.current; const tiles = map?._tiles;
    if (!map || !tiles) return;
    Object.values(tiles).forEach((t) => map.removeLayer(t));
    tiles[mapMode]?.addTo(map);
  }, [mapMode]);

  useEffect(() => {
    if (!mapReady) return; // wait for Leaflet to finish initialising
    const query = locationQuery || cityName;
    if (!query) return;

    const nominatim = (q) =>
      fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1&addressdetails=1`)
        .then((r) => r.json());

    const flyTo = (data) => {
      if (!data?.[0] || !mapObjRef.current) return false;
      const lat = parseFloat(data[0].lat), lng = parseFloat(data[0].lon);
      const c = { lat: Math.round(lat * 10000) / 10000, lng: Math.round(lng * 10000) / 10000 };

      // Use bounding box so country / state / city each zoom to the right level
      const bbox = data[0].boundingbox; // [south, north, west, east]
      if (bbox) {
        mapObjRef.current.fitBounds(
          [[parseFloat(bbox[0]), parseFloat(bbox[2])], [parseFloat(bbox[1]), parseFloat(bbox[3])]],
          { animate: true, padding: [30, 30] }
        );
      } else {
        mapObjRef.current.flyTo([lat, lng], 12, { duration: 1.5 });
      }

      markerRef.current?.setLatLng([lat, lng]);
      setCoords(c);
      setZone(`Showing: ${data[0].display_name?.split(",")[0] ?? query}`);
      onPinMove?.(c);
      return true;
    };

    const timer = setTimeout(() => {
      if (!mapObjRef.current) return;
      nominatim(query)
        .then((data) => {
          if (!flyTo(data) && query !== cityName) {
            return nominatim(cityName).then(flyTo);
          }
        })
        .catch(() => {});
    }, 600);

    return () => clearTimeout(timer);
  }, [locationQuery, cityName, mapReady]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative rounded-3xl overflow-hidden border-4 border-white/60 shadow-[0px_20px_40px_rgba(24,28,29,0.10)] h-full min-h-[460px]">
      <div ref={mapRef} className="absolute inset-0 z-0" style={{ cursor: "crosshair" }} />
      <div className="absolute top-4 left-4 z-10 pointer-events-none">
        <div className="flex items-center gap-2 bg-white/80 backdrop-blur-md px-4 py-2 rounded-xl border border-white/50 shadow-sm">
          <span className="w-2 h-2 rounded-full bg-[#8fc444] animate-pulse" />
          <p className="text-xs font-bold uppercase tracking-widest text-[#181c1d]">Satellite Live View</p>
        </div>
      </div>
      <div className="absolute right-4 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-2">
        <button onClick={() => mapObjRef.current?.zoomIn()}
          className="w-10 h-10 bg-white/80 backdrop-blur-md flex items-center justify-center text-[#181c1d] hover:bg-white transition-colors rounded-xl border border-white/50 shadow-sm">
          <span className="material-symbols-outlined text-[18px]">add</span>
        </button>
        <button onClick={() => mapObjRef.current?.zoomOut()}
          className="w-10 h-10 bg-white/80 backdrop-blur-md flex items-center justify-center text-[#181c1d] hover:bg-white transition-colors rounded-xl border border-white/50 shadow-sm">
          <span className="material-symbols-outlined text-[18px]">remove</span>
        </button>
        <div className="h-3" />
        <button className="w-10 h-10 bg-white/80 backdrop-blur-md flex items-center justify-center text-[#08757c] hover:bg-white transition-colors rounded-xl border border-white/50 shadow-sm">
          <span className="material-symbols-outlined text-[18px]">layers</span>
        </button>
      </div>
      {/* Zone Analysis — anchored bottom-left */}
      <div className="absolute bottom-4 left-4 z-10 pointer-events-none">
        <div className="bg-white/80 backdrop-blur-md p-4 rounded-2xl border border-white/50 shadow-sm max-w-xs pointer-events-auto">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#08757c] flex items-center justify-center text-white flex-shrink-0">
              <span className="material-symbols-outlined text-[18px]">location_on</span>
            </div>
            <div>
              <h4 className="font-bold text-xs text-[#181c1d]">Zone Analysis</h4>
              <p className="text-[10px] text-[#3e494a] mt-1 leading-relaxed">
                {zone ?? "Click on the map or drag the pin to set coordinates."}
              </p>
              <div className="mt-2 flex items-center gap-3">
                <div>
                  <p className="text-[9px] font-bold text-[#3e494a]/60 uppercase tracking-widest">Latitude</p>
                  <p className="text-xs font-mono font-bold text-[#181c1d]">{coords.lat}°</p>
                </div>
                <div className="w-px h-6 bg-[#bdc9c9]" />
                <div>
                  <p className="text-[9px] font-bold text-[#3e494a]/60 uppercase tracking-widest">Longitude</p>
                  <p className="text-xs font-mono font-bold text-[#181c1d]">{coords.lng}°</p>
                </div>
                <div className="w-px h-6 bg-[#bdc9c9]" />
                <div>
                  <p className="text-[9px] font-bold text-[#3e494a]/60 uppercase tracking-widest">Accuracy</p>
                  <p className="text-xs font-mono font-bold text-[#8fc444]">{accuracy}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Map mode switcher — anchored top-right independently */}
      <div className="absolute top-4 right-4 z-10">
        <div className="bg-white/80 backdrop-blur-md p-1.5 rounded-xl flex gap-1 border border-white/50 shadow-sm">
          {["map", "satellite", "terrain"].map((m) => (
            <button key={m} onClick={() => setMapMode(m)}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-colors ${
                mapMode === m ? "bg-[#8fc444] text-[#112000]" : "text-[#181c1d] hover:bg-[#e5e9e9]"
              }`}>
              {m}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

export default function LocationConfigCreate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isEditMode = location.state?.mode === "edit";
  const editData   = location.state?.data ?? {};

  const [isSaving,     setIsSaving]     = useState(false);
  const [saveError,    setSaveError]    = useState(null);
  const [saveSuccess,  setSaveSuccess]  = useState(false);
  const [errors,       setErrors]       = useState({});
  const [locationData, setLocationData] = useState(null);

  const [countryOptions,       setCountryOptions]       = useState([]);
  const [stateOptions,         setStateOptions]         = useState([]);
  const [cityOptions,          setCityOptions]          = useState([]);
  const [dropdownsInitialised, setDropdownsInitialised] = useState(false);
  const [mapCityName,          setMapCityName]          = useState("");

  // While true, the "clear city on country/state change" effects are
  // suppressed. Stays locked during the automated edit-mode chain-load and
  // only unlocks once the user actually picks a new Country/State manually.
  const editCityLockedRef = useRef(isEditMode);

  const [form, setForm] = useState(EMPTY_FORM);

  // ── Reset form when in CREATE mode ──────────────────────────────────────
  useEffect(() => {
    if (!isEditMode) {
      // CREATE mode: Clear everything for fresh form
      console.log("🆕 Create Mode - Resetting form");
      setForm(EMPTY_FORM);
      setStateOptions([]);
      setCityOptions([]);
      setLocationData(null);
      setErrors({});
      setMapCityName("");
      setDropdownsInitialised(false);
    }
  }, [isEditMode]);

  // ── Fetch full record when editing ───────────────────────────────────────
  // Calls: [HttpGet("Settings_GetLocationList")] with ID parameter
  useEffect(() => {
    if (!isEditMode || !editData?.id) {
      return;
    }
    setDropdownsInitialised(false);

    const locationId = editData.id;
    console.log("📥 Calling Settings_GetLocationList with ID:", locationId, "CompanyID:", companyID);

    dispatch(fetchLocations({ ID: locationId, companyID })).then((res) => {
      const result = toArray(res);
      if (result.length > 0) {
        const data = result[0];
        console.log("✅ Location fetched:", data);

        setLocationData(data);

        // Set form with ALL values from API
        const formData = {
          country: String(data.CountryID || ""),
          state: String(data.StateID || ""),
          city: String(data.CityID || ""),
          cityName: String(data.City_Name || ""),
          icon: null,
        };

        console.log("📝 Setting form:");
        console.log("   Country:", formData.country);
        console.log("   State:", formData.state);
        console.log("   City ID:", formData.city);
        console.log("   City Name:", formData.cityName, "Empty?", !formData.cityName);

        setForm(formData);
      } else {
        console.warn("⚠️ No location data returned for ID:", locationId);
      }
    }).catch((err) => {
      console.error("❌ Error fetching location:", err);
    });
  }, [isEditMode, editData?.id, companyID]); // eslint-disable-line react-hooks/exhaustive-deps

  const existingIconPath = useMemo(
    () =>
      locationData?.LocationIconPath ??
      locationData?.IconPath         ??
      locationData?.locationIconPath ??
      editData?.iconPath             ??
      null,
    [locationData, editData?.iconPath]
  );

  const existingIconName = useMemo(
    () => existingIconPath
      ? existingIconPath.split("/").pop().replace(/^[0-9a-f-]{36}_/i, "")
      : null,
    [existingIconPath]
  );

  // ── Load countries ────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchCountries()).then((res) =>
      setCountryOptions(toOptions(toArray(res), "Country_Name", "Country_Id"))
    );
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Edit: chain-load dropdowns ────────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || !locationData || countryOptions.length === 0) return;

    const countryId = locationData.CountryID || "";
    const stateId   = locationData.StateID || "";
    const cityId    = locationData.CityID || "";
    const cityName  = locationData.City_Name || "";

    console.log("🔄 Edit Mode Dropdowns Loading");
    console.log("Country:", countryId, "State:", stateId, "City:", cityId, "CityName:", cityName);

    if (countryId) {
      dispatch(fetchStates({ countryId })).then((res) => {
        const stateArr = toArray(res);
        setStateOptions(toOptions(stateArr, "State_Name", "State_Id"));
        console.log("✅ States loaded for Country:", countryId);

        if (stateId) {
          dispatch(fetchCities({ stateId })).then((cityRes) => {
            const cityArr = toArray(cityRes);
            const cityOptions = toOptions(cityArr, "City_Name", "CityID");

            console.log("✅ Cities loaded - Count:", cityArr.length);
            console.log("🎯 Verifying City:", { CityID: cityId, City_Name: cityName });

            setCityOptions(cityOptions);
            setMapCityName(cityName);
            // Only unblock the state/city "clear on change" effects once the
            // chain-load has actually finished — otherwise they race ahead of
            // this async data and wipe out the city we just set.
            setDropdownsInitialised(true);
          });
        } else {
          setDropdownsInitialised(true);
        }
      });
    } else {
      setDropdownsInitialised(true);
    }
  }, [isEditMode, locationData, countryOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Country → states ──────────────────────────────────────────────────────
  useEffect(() => {
    if (isEditMode && !dropdownsInitialised) return;
    // Suppressed while the edit-mode chain-load is in control (or hasn't
    // been unlocked by a manual Country change yet) — otherwise this refires
    // whenever countryOptions/dropdownsInitialised change and wipes the city.
    if (editCityLockedRef.current) return;
    if (!form.country) { setStateOptions([]); setCityOptions([]); return; }

    // In edit mode, only clear if we haven't initialized yet
    if (!isEditMode || !dropdownsInitialised) {
      setForm((prev) => ({ ...prev, state: "", city: "", cityName: "" }));
      setCityOptions([]);
      setMapCityName("");
    }

    dispatch(fetchStates({ countryId: form.country })).then((res) => {
      const states = toArray(res);
      let stateOpts = toOptions(states, "State_Name", "State_Id");

      // If no states found, show country name as state option and pre-fill
      if (stateOpts.length === 0) {
        const countryLabel = countryOptions.find((o) => String(o.value) === String(form.country))?.label;
        if (countryLabel) {
          stateOpts = [{ label: countryLabel, value: form.country }];
          setForm((prev) => ({ ...prev, state: form.country, city: form.country, cityName: "" }));
          setCityOptions([{ label: countryLabel, value: form.country }]);
          setMapCityName(countryLabel);
        }
      }

      setStateOptions(stateOpts);
    });
  }, [form.country, countryOptions, isEditMode, dropdownsInitialised]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync city value after options load ─────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || !form.city || !cityOptions.length) {
      console.log("⏭️ City sync skipped - EditMode:", isEditMode, "CityID:", form.city, "Options:", cityOptions.length);
      return;
    }

    console.log("🔍 Syncing city after options load - Looking for:", form.city);
    console.log("📋 Available options:", cityOptions);

    const matched = cityOptions.find((o) => String(o.value) === String(form.city));

    if (matched) {
      console.log("✅ City found in options:", matched.label);
      setMapCityName(matched.label);
      // Ensure form has the cityName as well
      setForm((prev) => ({
        ...prev,
        cityName: matched.label
      }));
    } else {
      console.warn("⚠️ City ID not found in options - CityID:", form.city, "Available IDs:", cityOptions.map(o => o.value));
    }
  }, [cityOptions, isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── State → cities ───────────────────────────────────────────────────────
  useEffect(() => {
    if (isEditMode && !dropdownsInitialised) return;
    // Suppressed while the edit-mode chain-load is in control (or hasn't
    // been unlocked by a manual State change yet) — otherwise this refires
    // whenever stateOptions/dropdownsInitialised change and wipes the city.
    if (editCityLockedRef.current) return;
    if (!form.state) { setCityOptions([]); return; }

    // Always clear previous city selection when state changes
    setForm((prev) => ({ ...prev, city: "", cityName: "" }));
    setMapCityName("");

    dispatch(fetchCities({ stateId: form.state })).then((res) => {
      const cities = toArray(res);
      let cityOpts = toOptions(cities, "City_Name", "City_Id");

      // If no cities found, show state name as fallback city and set it as NEW city
      if (cityOpts.length === 0) {
        const stateLabel = stateOptions.find((o) => String(o.value) === String(form.state))?.label;
        if (stateLabel) {
          // Don't create a fake option - just set the state name as the city name
          // This way, saving will send CityID = 0 and CityName = state name
          console.log("⚠️ No cities found for state:", stateLabel, "- treating as new city");
          setForm((prev) => ({ ...prev, city: "0", cityName: stateLabel }));
          setMapCityName(stateLabel);
        }
      }

      setCityOptions(cityOpts);
    });
  }, [form.state, stateOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!form.city || form.city === "0" || cityOptions.length === 0) return;
    const cityLabel = cityOptions.find((o) => String(o.value) === String(form.city))?.label;
    if (cityLabel) setMapCityName(cityLabel);
  }, [form.city, cityOptions]);

  useEffect(() => {
    if (form.cityName && form.city === "0") setMapCityName(form.cityName);
  }, [form.cityName, form.city]);

  const mapLocationQuery = useMemo(() => {
    const parts = [mapCityName];
    const stateLabel   = stateOptions.find((o) => String(o.value) === String(form.state))?.label;
    const countryLabel = countryOptions.find((o) => String(o.value) === String(form.country))?.label;
    if (stateLabel)   parts.push(stateLabel);
    if (countryLabel) parts.push(countryLabel);
    return parts.filter(Boolean).join(", ");
  }, [mapCityName, form.state, form.country, stateOptions, countryOptions]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleField = useCallback((field) => (val) => {
    const value = val?.target ? val.target.value : val;
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    setSaveSuccess(false);
    setSaveError(null);
  }, []);

  // Manual Country/State selection — unlocks the "clear city" effects so a
  // real user change still clears the previously loaded city as expected.
  const handleCountryChange = useCallback((val) => {
    editCityLockedRef.current = false;
    handleField("country")(val);
  }, [handleField]);

  const handleStateChange = useCallback((val) => {
    editCityLockedRef.current = false;
    handleField("state")(val);
  }, [handleField]);

  const handleCityChange = useCallback((cityId) => {
    setForm((prev) => ({ ...prev, city: cityId }));
    setErrors((prev) => ({ ...prev, city: "" }));
    setSaveSuccess(false);
    setSaveError(null);
  }, []);

  const handleNewCity = useCallback((cityName) => {
    setForm((prev) => ({ ...prev, cityName: cityName ?? "" }));
  }, []);

  // ── Icon handler — with type + 5MB size restriction ──────────────────────
  const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"];
  const handleIcon = useCallback((file) => {
    if (!file) {
      setSaveError(null);
      setForm((prev) => ({ ...prev, icon: null }));
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setSaveError("Only PNG, JPG, JPEG, and SVG files are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setSaveError("Image file size must be under 5 MB. Please choose a smaller file.");
      return;
    }
    setSaveError(null);
    setForm((prev) => ({ ...prev, icon: file }));
    setSaveSuccess(false);
  }, []);

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!form.country) errs.country = "Country is required.";
    if (!form.state) errs.state = "State is required.";

    // In EDIT mode: City already exists in database, don't validate
    // In CREATE mode: City must be selected or typed
    if (!isEditMode) {
      const hasValidCity = (form.city && form.city !== "0" && form.city !== "") || (form.cityName && form.cityName.trim());
      if (!hasValidCity) {
        errs.city = "City is required.";
      }
    }

    return errs;
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const formData = new FormData();
    formData.append("ID",                             isEditMode ? editData.id ?? 0 : 0);
    formData.append("LocationModel.CountryID",        form.country);
    formData.append("LocationModel.StateID",          form.state);

    // Send logic:
    // - If cityName has a value (user typed or auto-filled state name) → CityID = 0, CityName = value
    // - Otherwise → CityID = form.city, CityName = ""
    // This ensures new cities (typed or state names) get CityID = 0
    const hasCityName = form.cityName && form.cityName.trim() !== "";
    const cityID = hasCityName ? "0" : (form.city || "0");
    const cityName = hasCityName ? form.cityName.trim() : "";

    console.log("💾 Saving Location:");
    console.log("   CityID:", cityID, "(0 = new city)");
    console.log("   CityName:", cityName || "(empty - using existing)");
    console.log("   form.city:", form.city, "form.cityName:", form.cityName);

    formData.append("LocationModel.CityID",           cityID);
    formData.append("LocationModel.CityName",         cityName);
    formData.append("LocationModel.LocationIconPath", existingIconPath ?? "");
    formData.append("CreatedBy",                      employeeID);
    formData.append("CompanyID",                      companyID);

    if (form.icon) {
      formData.append("locationImage", form.icon);
    } else if (isEditMode && existingIconPath) {
      try {
        const res  = await fetch(`${URL}${existingIconPath}`);
        const blob = await res.blob();
        const name = existingIconPath.split("/").pop();
        formData.append("locationImage", blob, name);
      } catch {
        formData.append("locationImage", new Blob([], { type: "image/png" }), "empty.png");
      }
    } else {
      formData.append("locationImage", new Blob([], { type: "image/png" }), "empty.png");
    }

    setIsSaving(true); setSaveError(null); setSaveSuccess(false);

    dispatch(saveLocation(formData))
      .then((res) => {
        const result = res?.payload?.data ?? res?.payload;
        if (Number(result) === -1) {
          setSaveError("This location already exists.");
        } else if (Number(result) > 0) {
          if (isEditMode) {
            navigate("/application-config", { state: { tab: "location", refresh: true, editSuccess: true } });
          } else {
            // Create mode - reset form after successful save
            console.log("✅ Location saved successfully - clearing form");
            setSaveSuccess(true);
            setForm(EMPTY_FORM);
            setStateOptions([]);
            setCityOptions([]);
            setErrors({});
            setMapCityName("");
            setDropdownsInitialised(false);
            setTimeout(() => setSaveSuccess(false), 4000);
          }
        } else {
          setSaveError("Failed to save. Please try again.");
        }
      })
      .catch((err) => setSaveError(err?.message ?? "Failed to save location"))
      .finally(() => setIsSaving(false));
  }, [form, isEditMode, editData, existingIconPath, dispatch, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = useCallback(
    () => navigate("/application-config", { state: { tab: "location", refresh: true } }),
    [navigate]
  );

  const saveLabel = isEditMode ? "Update Location" : "Save Location";

  const SaveButton = () => (
    <button onClick={handleSave} disabled={isSaving}
      className="px-8 py-2.5 bg-gradient-to-br from-[#08757c] to-[#005b60] text-white text-sm font-bold rounded-xl shadow-lg shadow-[#08757c]/20 hover:opacity-90 active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
      {isSaving
        ? <><span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>Saving…</>
        : <><span className="material-symbols-outlined text-[18px]">save</span>{saveLabel}</>}
    </button>
  );

  const CancelButton = () => (
    <button onClick={handleCancel}
      className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-slate-600 font-bold text-sm hover:bg-slate-100 transition-colors">
      <span className="material-symbols-outlined text-lg">arrow_back</span>
      Back to List
    </button>
  );

  return (
    <DashboardLayout>
      <div className="px-8 py-5 bg-[#f7fafa] min-h-screen text-[#181c1d]">
        <div className="max-w-[1400px] mx-auto">

          {/* ── Header ── */}
          <div className="mb-5 flex justify-between items-end">
            <div>
              <nav className="flex items-center gap-2 text-xs text-[#3e494a] mb-2">
                <button onClick={handleCancel} className="hover:text-[#08757c] transition-colors">App Configuration</button>
                <span className="material-symbols-outlined text-[14px]">chevron_right</span>
                <button onClick={handleCancel} className="hover:text-[#08757c] transition-colors">Location Management</button>
                <span className="material-symbols-outlined text-[14px]">chevron_right</span>
                <span className="text-[#08757c] font-semibold">{isEditMode ? "Edit Location" : "Add New Location"}</span>
              </nav>
              <button onClick={handleCancel}
                className="group flex items-center gap-1.5 text-[#08757c] text-sm font-semibold mb-2 hover:opacity-75 transition-opacity">
                <span className="material-symbols-outlined text-[18px] transition-transform group-hover:-translate-x-1">arrow_back</span>
                Back to Location List
              </button>
              <h1 className="text-3xl font-extrabold tracking-tight text-[#181c1d]">
                {isEditMode ? "Edit Location" : "Add New Location"}
              </h1>
              <p className="text-[#3e494a] text-sm mt-1 max-w-xl">
                Configure and manage location details for your company.
                Assign country, state, and city to organize your site locations.
              </p>
            </div>
            <div className="flex gap-3"><CancelButton /><SaveButton /></div>
          </div>

          {/* ── Content Grid ── */}
          <div className="grid grid-cols-12 gap-6">

            {/* ── Left: Form ── */}
            <div className="col-span-12 lg:col-span-5 space-y-4">

              {/* Location Identity */}
              <div className="bg-white p-5 rounded-2xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/40">
                <h3 className="text-base font-bold flex items-center gap-2 mb-4">
                  <span className="material-symbols-outlined text-[#08757c] text-[20px]">location_on</span>
                  Location Identity
                </h3>
                <div className="space-y-4">
                  <div>
                    <FieldLabel required>Country</FieldLabel>
                    <Dropdown options={countryOptions} value={form.country}
                      onChange={handleCountryChange}
                      placeholder="Select Country" searchPlaceholder="Search country..."
                      error={errors.country}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <FieldLabel required>State / Region</FieldLabel>
                      <Dropdown options={stateOptions} value={form.state}
                        onChange={handleStateChange}
                        placeholder="Select State" searchPlaceholder="Search state..."
                        disabled={!form.country} error={errors.state}
                      />
                    </div>
                    <div>
                      <FieldLabel required>City</FieldLabel>
                      <CityAutocomplete
                        options={cityOptions}
                        value={form.city}
                        cityName={form.cityName}
                        onChange={handleCityChange}
                        onNewCity={handleNewCity}
                        disabled={!form.state}
                        error={errors.city}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── City Icon Card ── */}
              <div className="bg-white p-5 rounded-2xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/40">
                <h3 className="text-base font-bold flex items-center gap-2 mb-3">
                  <span className="material-symbols-outlined text-[#08757c] text-[20px]">image</span>
                  City Icon
                </h3>

                {/* Recommendations panel */}
                <div className="mb-5 bg-[#f7fafa] rounded-xl p-4 border border-[#e5e9e9]">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#3e494a] flex items-center gap-1 mb-3">
                    <span className="material-symbols-outlined text-[14px] text-[#08757c]">tips_and_updates</span>
                    Image Recommendations
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { icon: "straighten",               label: "Minimum Size",  value: "400 × 700 px"    },
                      { icon: "aspect_ratio",             label: "Ideal Size",    value: "600 × 1050 px"   },
                      { icon: "photo_size_select_actual", label: "Max File Size", value: "5 MB"            },
                      { icon: "palette",                  label: "Formats",       value: "PNG · JPG · JPEG · SVG"},
                      { icon: "crop_portrait",            label: "Orientation",   value: "Portrait (4:7)"  },
                      { icon: "center_focus_strong",      label: "Focus",         value: "Center of image" },
                    ].map(({ icon, label, value }) => (
                      <div key={label} className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-[#08757c] text-[16px]">{icon}</span>
                        <div>
                          <p className="text-[9px] font-bold text-[#3e494a]/60 uppercase tracking-widest">{label}</p>
                          <p className="text-xs font-semibold text-[#181c1d]">{value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-[#ba1a1a] flex items-start gap-1 mt-3 pt-3 border-t border-[#e5e9e9]">
                    <span className="material-symbols-outlined text-[13px] flex-shrink-0 mt-0.5">warning</span>
                    Use a portrait image with the main subject centered. Screenshots or wide landscape images may appear cropped or blurry on cards.
                  </p>
                </div>

                {/* Existing icon preview in edit mode — object-contain so full image is visible */}
                {isEditMode && existingIconPath && !form.icon && (
                  <div className="mb-4 space-y-2">
                    <a href={`${URL}${existingIconPath}`} target="_blank" rel="noreferrer"
                      className="flex items-center gap-3 bg-[#f1f4f4] border border-[#bdc9c9]/30 px-3 py-2 rounded-lg w-fit hover:bg-[#e5e9e9] hover:border-[#08757c]/30 transition-colors group">
                      <img
                        src={`${URL}${existingIconPath}`}
                        alt={existingIconName ?? "city icon"}
                        className="w-10 h-10 rounded-lg flex-shrink-0 border border-[#bdc9c9]/30 bg-[#f7fafa]"
                        style={{ objectFit: "contain" }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          if (e.target.nextSibling) e.target.nextSibling.style.display = "inline";
                        }}
                      />
                      <span className="material-symbols-outlined text-[18px] text-[#08757c]" style={{ display: "none" }}>image</span>
                      <span className="text-xs font-medium text-[#3e494a] truncate max-w-xs group-hover:text-[#08757c] transition-colors">
                        {existingIconName}
                      </span>
                      <span className="material-symbols-outlined text-[14px] text-[#3e494a]/40 group-hover:text-[#08757c] transition-colors">open_in_new</span>
                    </a>
                    <p className="text-[10px] text-[#3e494a]/60">Upload a new icon below to replace the existing one.</p>
                  </div>
                )}

                {/* FileUpload — onChange={handleIcon} kept exactly as original */}
                <FileUpload
                  value={form.icon}
                  onChange={handleIcon}
                  accept="image/png,image/jpg,image/jpeg,image/svg+xml"
                      hint="PNG · JPG · JPEG · SVG · Portrait 2:3 · Min 400×560px · Max 5MB"
                />
              </div>

              {/* Validation note */}
              <div className="bg-[#f1f4f4] p-3 rounded-xl flex gap-3 items-center border border-[#bdc9c9]/10">
                <span className="material-symbols-outlined text-[#08757c] p-2 bg-white rounded-xl text-[20px]">verified_user</span>
                <div>
                  <h5 className="text-[10px] font-bold uppercase tracking-widest text-[#3e494a]">Validation</h5>
                  <p className="text-xs text-[#181c1d] mt-0.5">Country, State, and City are mandatory fields.</p>
                </div>
              </div>

              {saveSuccess && (
                <div className="bg-[#bbf46e]/20 text-[#436900] rounded-xl px-5 py-4 text-sm flex items-center gap-3 border border-[#bbf46e]/40">
                  <span className="material-symbols-outlined text-[20px] text-[#436900]">check_circle</span>
                  <span className="font-semibold">Location created successfully. You can add another one.</span>
                </div>
              )}
              {saveError && (
                <div className="bg-red-50 text-[#ba1a1a] rounded-xl px-4 py-3 text-sm flex items-center gap-3 mb-5">
                  <span className="material-symbols-outlined text-[20px]">error</span>
                  {saveError}
                </div>
              )}
            </div>

            {/* ── Right: Map ── */}
            <div className="col-span-12 lg:col-span-7">
              <MapPanel cityName={mapCityName} locationQuery={mapLocationQuery} onPinMove={(coords) => console.log("[Map] pin:", coords)} />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-5">
            <CancelButton />
            <SaveButton />
          </div>

        </div>
      </div>
    </DashboardLayout>
  );
}
